import type { MetaResponse, SubmissionRow } from '../api/client';
import {
  PARTICIPANT_FORMS,
  endorsable,
  endorseCopy,
  endorseTargetsFor,
  studySlugOf,
} from '../lib/pipeline';
import { Confirm, pillSelectStyle, selectStyle } from './ui';

/**
 * Endorse to… — hand a person to another study's team.
 *
 * One control for the record's footer, the Forms submission rows and the
 * board cards, so all three ask the question in the same words. It is not a
 * status choice: the row changes study, lands in that board's Newly endorsed
 * column, and stays on this one's Endorsed out
 * (docs/api-contract-endorsements.md).
 *
 * Presentational. The parent owns which bubble is open (`target`, so a screen
 * with one per row never has two up), sends the request, and shows the
 * outcome where its screen shows outcomes: the record closes, the inbox says
 * where the person went, the board shows them under Endorsed out.
 *
 * Disabled rather than hidden when it cannot work, with the reason on hover.
 * An API older than 035 records no endorsements, and a row with no study has
 * nothing to be endorsed from — the API answers 409 — so the inbox's study
 * picker is the step before.
 */
export function EndorseControl({
  row,
  meta,
  target,
  onTarget,
  onEndorse,
  pending,
  size = 'field',
  align = 'left',
}: {
  row: SubmissionRow;
  meta: MetaResponse;
  /** The study whose bubble is open; null is the placeholder, nothing asked. */
  target: string | null;
  /** The select changed, or the bubble was cancelled (null). */
  onTarget: (study: string | null) => void;
  /** Confirmed. */
  onEndorse: (study: string) => void;
  pending: boolean;
  /** A field beside the record's other controls, or a pill on a row or card. */
  size?: 'field' | 'pill';
  align?: 'left' | 'right';
}) {
  // Only the four participant pathways have a study to be handed over from.
  if (!(PARTICIPANT_FORMS as readonly string[]).includes(row.form)) return null;

  const targets = endorseTargetsFor(meta, row);
  const chosen = targets.find((t) => t.key === target);
  const recording = endorsable(meta);
  const noStudy = studySlugOf(row) === null;
  const disabled = pending || !recording || noStudy || targets.length === 0;
  const title = !recording
    ? 'Needs the API update that records endorsements.'
    : noStudy
      ? 'Pick a study first.'
      : `Hand ${row.name} to another study's team`;
  const copy = chosen ? endorseCopy(meta, row.name, chosen) : null;

  return (
    <Confirm
      open={!!chosen}
      title={copy?.title ?? ''}
      detail={copy?.detail}
      confirmLabel="Endorse"
      align={align}
      onConfirm={() => chosen && onEndorse(chosen.key)}
      onCancel={() => onTarget(null)}
    >
      <select
        value={target ?? ''}
        onChange={(e) => onTarget(e.target.value || null)}
        disabled={disabled}
        title={title}
        aria-label="Endorse to another study"
        style={
          size === 'pill'
            ? {
                ...pillSelectStyle,
                // A native select sizes itself to its widest option, which
                // would push the pill under Accept on a row; the bubble names
                // the chosen study, so the pill can stay narrow.
                width: 150,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }
            : { ...selectStyle, width: 210, fontSize: 13 }
        }
      >
        <option value="">Endorse to…</option>
        {targets.map((s) => (
          <option key={s.key} value={s.key}>
            {s.short} · {s.status}
          </option>
        ))}
      </select>
    </Confirm>
  );
}
